/**
 * The page.
 *
 * It scores one text with three detectors. The headline says whether a machine wrote it, and two
 * sub-readings say which of the two writing tools it looks like.
 *
 * ## The page is deliberately a toy
 *
 * **The owner decided this on 2026-08-18.** An earlier build printed the score, the calibration
 * table, the training sizes, and a four-bullet caveat. Item 25 had already found that the tool is
 * fit for education and amusement only, and a wall of statistics dressed a toy as an instrument.
 *
 * So the page now shows a dial and a sentence, and the write-up carries the detail. The footer
 * keeps the two facts a reader cannot do without: the tool knows two AI writing tools, and it is
 * not evidence about any person.
 *
 * ## Why three readings, and why one of them leads
 *
 * The build shipped two detectors until 2026-08-18, and the owner withdrew the second: it put a
 * `probability` beside a `ranking`, so the two cards invited a comparison that failed.
 *
 * Item 36 removed that objection. The three detectors read the same 519 human passages, the same
 * folds, and the same procedure, and only the AI half differs. So they do compare.
 *
 * **They are still three separate questions, and they do not sum to one.** A reader can see a
 * robot score of 0.85 above a Claude score of 0.30 and a ChatGPT score of 0.40, and nothing is
 * wrong. The layout carries that rather than a sentence: the verdict takes the large type, and
 * the two models sit in a quieter row under a heading that asks which one it looks like.
 *
 * A line saying the three do not add up stood here until 2026-08-19. The owner removed it. It
 * described a property of the numbers, and it told a visitor nothing they could act on.
 *
 * The verdict is its own classifier, trained on both models at once. It is not the two
 * sub-readings combined: a combination compounds the false alarms of each, and it carries no
 * calibration, so no sentence about what a score meant could read it.
 */

import { fetchDetectors, modelsOf, verdictOf as headlineOf } from './detector/load';
import { scoreText } from './detector/model';
import { letterCount } from './detector/normalise';
import type { Detector } from './detector/types';
import { MIN_LETTERS, meterCurve, strengthOf } from './strength';
import { markOf } from './marks';
import { sideNote, verdictOf } from './verdict';

import './styles.css';

/** Where the write-up lives. The page carries no explanation of its own. */
const POST_URL = 'https://www.atomic14.com/2026/08/18/detecting-claude-with-letter-counts.html';

const textBox = document.querySelector<HTMLTextAreaElement>('#text')!;
const button = document.querySelector<HTMLButtonElement>('#score')!;
const lengthLabel = document.querySelector<HTMLParagraphElement>('#length')!;
const results = document.querySelector<HTMLElement>('#result')!;
const postLink = document.querySelector<HTMLAnchorElement>('#post-link')!;
const meterBars = document.querySelector<HTMLElement>('#meter-bars')!;
const meterLabel = document.querySelector<HTMLElement>('#meter-label')!;
const meterNudge = document.querySelector<HTMLElement>('#meter-nudge')!;

/** How many bars the meter draws. */
const BARS = 5;

let verdict: Detector | null = null;
let models: Detector[] = [];

/** Return an element with a class and text already set. */
function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text = '',
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * Update the letter count, the strength meter, and the state of the button.
 *
 * The page scores any text of `MIN_LETTERS` or more. It used to refuse below 432 letters, and a
 * refusal tells a reader nothing. The meter says how much the answer is worth instead, and the
 * measurement behind it ships in the artefact.
 */
function refreshLength(): void {
  const letters = letterCount(textBox.value);
  lengthLabel.textContent = `${letters.toLocaleString()} letters`;

  if (!verdict) return;
  // The meter reads the two model detectors, and never the pooled one. See `meterCurve`: the
  // pooled curve peaks below the top band, so a meter driven by it would ask for far more text
  // than the page needs.
  const strength = strengthOf(letters, meterCurve(models.map((one) => one.meta)));

  meterBars.replaceChildren();
  for (let i = 0; i < BARS; i += 1) {
    const lit = i < strength.bars;
    meterBars.append(element('span', `meter__bar${lit ? ` meter__bar--${strength.tone}` : ''}`));
  }
  meterLabel.textContent = strength.label;
  meterNudge.textContent = strength.nudge ?? '';

  button.disabled = letters < MIN_LETTERS;
  button.textContent = letters < MIN_LETTERS ? 'Give it something to read' : 'Robot or meatbag?';
}

/** Build the dial, with the marker at the position of the score. */
function dial(score: number, tone: string): HTMLElement {
  const box = element('div', 'dial');
  const track = element('div', 'dial__track');

  const marker = element('div', `dial__marker dial__marker--${tone}`);
  // The marker is centred on its own position, so the travel is inset by half its width at each
  // end. Without that a score of 0 or 1 hangs over the end of the track.
  marker.style.left = `calc(11px + (100% - 22px) * ${Math.min(1, Math.max(0, score))})`;
  track.append(marker);

  const ends = element('div', 'dial__ends');
  ends.append(
    element('span', 'dial__end', '🧠\u00a0meatbag'),
    element('span', 'dial__end', 'robot\u00a0🤖'),
  );

  box.append(track, ends);
  return box;
}

/** Build the result. */
function card(one: Detector, text: string): HTMLElement {
  if (!models.length) throw new Error('the card needs the model detectors for its meter');
  const reading = scoreText(one, text);
  const box = element('section', 'card');
  const verdict = verdictOf(reading.score);
  box.classList.add(`card--${verdict.tone}`);
  box.append(
    element('p', 'card__emoji', verdict.emoji),
    element('p', 'card__label', verdict.label),
    dial(reading.score, verdict.tone),
    element('p', 'card__aside', verdict.aside),
    element('p', 'card__note', sideNote(reading.score, one.meta)),
  );

  // The same curve the meter reads, so the two never disagree. They did once: the meter said
  // "About as good as it gets" while the note under it asked for a bit more text.
  const strength = strengthOf(reading.letters, meterCurve(models.map((each) => each.meta)));
  if (strength.nudge) box.append(element('p', 'card__note', strength.nudge));

  return box;
}

/** Show a message in place of the result. */
function showMessage(heading: string, detail: string): void {
  results.hidden = false;
  results.replaceChildren();
  const box = element('section', 'card card--short');
  box.append(
    element('p', 'card__emoji', '💥'),
    element('p', 'card__label', heading),
    element('p', 'card__aside', detail),
  );
  results.append(box);
}

/**
 * Build one sub-reading: a mark, a name, a small dial, and the number.
 *
 * A sub-reading carries no verdict words of its own. "Written by a robot" belongs to the headline
 * alone, and repeating a verdict here would give a reader three answers to one question.
 */
function subReading(one: Detector, text: string): HTMLElement {
  const reading = scoreText(one, text);
  const row = element('div', 'sub');

  const mark = markOf(one.meta.slug);
  const name = element('div', 'sub__name');
  if (mark) name.append(mark);
  name.append(element('span', 'sub__label', one.meta.detected_model));

  row.append(name, dial(reading.score, verdictOf(reading.score).tone));
  row.append(element('span', 'sub__score', reading.score.toFixed(2)));
  return row;
}

/** Score the text with every detector. */
function run(): void {
  if (!verdict) return;
  results.hidden = false;

  const parts: HTMLElement[] = [card(verdict, textBox.value)];
  if (models.length) {
    const box = element('section', 'models');
    box.append(element('h2', 'models__heading', 'Which one does it look like?'));
    for (const one of models) box.append(subReading(one, textBox.value));
    parts.push(box);
  }

  results.replaceChildren(...parts);
  results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

async function start(): Promise<void> {
  postLink.href = POST_URL;
  textBox.addEventListener('input', refreshLength);
  button.addEventListener('click', run);
  refreshLength();

  try {
    const built = await fetchDetectors('model', (fraction: number) => {
      button.textContent = `Warming up the machine… ${Math.round(fraction * 100)}%`;
    });
    verdict = headlineOf(built);
    models = modelsOf(built);
    if (!verdict) throw new Error('no detector carries the verdict role');
  } catch (error) {
    button.textContent = 'The machine broke';
    showMessage('The machine broke', error instanceof Error ? error.message : String(error));
    return;
  }

  refreshLength();
}

void start();
